import axios from 'axios';
import { toast } from "../components/ThreatModelling/ui/use-toast";
import { User } from '../types/auth';
import { getUserData } from '../utils/encryption';
import { hasValidSecurityTrial, getTrialStatusMessage } from '../utils/trialUtils';
import { error, log } from '../utils/logger';
import { getAccessToken } from './tokenService';
import { joinUrl, normalizeUrl } from 'lib/utils';

const SECURITY_BACKEND_URL = normalizeUrl(process.env.REACT_APP_BACKEND_URL || 'http://localhost:9017');

/**
 * Create headers for security API requests (JWT only)
 */
export const createSecurityHeaders = (): Record<string, string> | null => {
  const token = getAccessToken();
  if (!token) {
    log("createSecurityHeaders: Missing token");
    return null;
  }

  try {
    // Get user data for basic headers
    const user = getUserData();
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    // Add user/org headers if available
    if (user) {
      headers['x-user-id'] = user.user_id?.toString() || '';
      headers['x-org-id'] = user.org_names_list?.[0] || user.email || '';
    }

    return headers;
  } catch (err: any) {
    error('Error in createSecurityHeaders:', err);
    // Return basic auth header as fallback
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }
};

/**
 * No frontend validation - backend handles trial validation from JWT
 */
const validateTrialAccess = (user: User | null): boolean => {
  if (!user) {
    toast({
      variant: "destructive",
      title: "Authentication Required",
      description: "Please log in to access security features."
    });
    return false;
  }

  // No frontend trial validation - backend handles it
  return true;
};

/**
 * Security Analysis (Threat Model) API
 */
export const generateSecurityAnalysis = async (projectId: number, firstName: string, lastName: string): Promise<{ id: number }> => {
  try {
    const user = getUserData();
    if (!validateTrialAccess(user)) {
      throw new Error('Trial access validation failed');
    }

    const headers = createSecurityHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(SECURITY_BACKEND_URL, 'api/security_analysis'), {
      project_id: projectId,
      first_name: firstName,
      last_name: lastName
    }, {
      headers
    });

    toast({
      title: "Success",
      description: "Security analysis initiated successfully!"
    });
    return response.data;
  } catch (err: any) {
    // Handle trial-specific errors
    if (err.response?.status === 403) {
      const errorMessage = err.response?.data?.error || 'Access denied. Please check your trial status.';
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: errorMessage
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.response?.data?.error || 'Failed to generate security analysis.'
      });
    }
    throw err;
  }
};

/**
 * SBOM Scan API with trial validation
 */
export const generateSBOMScanWithTrial = async (projectId: number, firstName: string, lastName: string, excludedFolders?: string[]): Promise<{ id: number }> => {
  try {
    const user = getUserData();
    if (!validateTrialAccess(user)) {
      throw new Error('Trial access validation failed');
    }

    const headers = createSecurityHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(SECURITY_BACKEND_URL, 'api/sbom'), {
      project_id: projectId,
      first_name: firstName,
      last_name: lastName,
      excluded_folders: excludedFolders
    }, {
      headers
    });

    toast({
      title: "Success",
      description: "SBOM scan initiated successfully!"
    });
    return response.data;
  } catch (err: any) {
    if (err.response?.status === 403) {
      const errorMessage = err.response?.data?.error || 'Access denied. Please check your trial status.';
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: errorMessage
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.response?.data?.error || 'Failed to generate SBOM scan.'
      });
    }
    throw err;
  }
};

/**
 * SCA Scan API with trial validation
 */
export const generateSCAScanWithTrial = async (projectId: number, firstName: string, lastName: string, excludedFolders?: string[]): Promise<{ id: number }> => {
  try {
    const user = getUserData();
    if (!validateTrialAccess(user)) {
      throw new Error('Trial access validation failed');
    }

    const headers = createSecurityHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(SECURITY_BACKEND_URL, 'api/sca'), {
      project_id: projectId,
      first_name: firstName,
      last_name: lastName,
      excluded_folders: excludedFolders
    }, {
      headers
    });

    toast({
      title: "Success",
      description: "SCA scan initiated successfully!"
    });
    return response.data;
  } catch (err: any) {
    if (err.response?.status === 403) {
      const errorMessage = err.response?.data?.error || 'Access denied. Please check your trial status.';
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: errorMessage
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.response?.data?.error || 'Failed to generate SCA scan.'
      });
    }
    throw err;
  }
};

/**
 * Secrets Scan API with trial validation
 */
export const generateSecretsScanWithTrial = async (projectId: number, firstName: string, lastName: string, excludedFolders?: string[]): Promise<{ id: number }> => {
  try {
    const user = getUserData();
    if (!validateTrialAccess(user)) {
      throw new Error('Trial access validation failed');
    }

    const headers = createSecurityHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(SECURITY_BACKEND_URL, 'api/secrets'), {
      project_id: projectId,
      first_name: firstName,
      last_name: lastName,
      excluded_folders: excludedFolders
    }, {
      headers
    });

    toast({
      title: "Success",
      description: "Secrets scan initiated successfully!"
    });
    return response.data;
  } catch (err: any) {
    if (err.response?.status === 403) {
      const errorMessage = err.response?.data?.error || 'Access denied. Please check your trial status.';
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: errorMessage
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.response?.data?.error || 'Failed to generate secrets scan.'
      });
    }
    throw err;
  }
};

/**
 * Threat Model API with trial validation
 */
export const generateThreatModelWithTrial = async (projectId: number): Promise<{ id: number }> => {
  try {
    const user = getUserData();
    if (!validateTrialAccess(user)) {
      throw new Error('Trial access validation failed');
    }

    const headers = createSecurityHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(SECURITY_BACKEND_URL, 'api/threat_model'), {
      project_id: projectId
    }, {
      headers
    });

    toast({
      title: "Success",
      description: "Threat model scan initiated successfully!"
    });
    return response.data;
  } catch (err: any) {
    if (err.response?.status === 403) {
      const errorMessage = err.response?.data?.error || 'Access denied. Please check your trial status.';
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: errorMessage
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.response?.data?.error || 'Failed to generate threat model.'
      });
    }
    throw err;
  }
};

/**
 * Check if user has valid trial access (utility function for UI)
 */
export const checkTrialAccess = (): boolean => {
  const user = getUserData();
  return hasValidSecurityTrial(user);
};

/**
 * Get trial status for UI display
 */
export const getTrialStatus = (): { hasAccess: boolean; message: string | null } => {
  const user = getUserData();
  return {
    hasAccess: hasValidSecurityTrial(user),
    message: getTrialStatusMessage(user)
  };
};
